// Role-filtered user picker used by the pill widget
// popover + the Underwriting tab Section 3 / Section 9 assignment forms.
//
// Backed by the shared useAssignableUsers hook (one unfiltered
// /api/users/assignable fetch, role filtering client-side — so several
// pickers with different roles on one page share a single request).
// Renders a vanilla <select>; the picker is short (typically
// <50 users per role) so a combobox is unnecessary v1.

import { Select } from "@/components/ui/Select";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";

interface RoleFilteredUserPickerProps {
  role: string;
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  /** Optional placeholder text shown as the "no selection" option. */
  placeholder?: string;
  id?: string;
  "aria-label"?: string;
}

export function RoleFilteredUserPicker({
  role,
  value,
  onChange,
  disabled = false,
  placeholder = "Select user…",
  id,
  "aria-label": ariaLabel,
}: RoleFilteredUserPickerProps) {
  const usersQuery = useAssignableUsers(role);

  return (
    <Select
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled || usersQuery.isLoading}
      aria-label={ariaLabel}
    >
      <option value="">
        {usersQuery.isLoading ? "Loading…" : placeholder}
      </option>
      {(usersQuery.data ?? []).map((u) => (
        <option key={u.id} value={u.id}>
          {u.displayName}
        </option>
      ))}
    </Select>
  );
}
